// SZOLGÁLTATÁSOK — a hozzárendelések és a katalógus EGY lapon, két fülön.
//
// MIÉRT EGY LAP: a hozzárendelés ügyfélhez/fiókhoz tartozik (a menüben ezért ül az Ügyfélkapcsolat alatt), a
// katalógus pedig a hozzárendelés SZÓTÁRA — hozzárendelést úgy viszel fel, hogy katalógus-szolgáltatást
// választasz. Együtt használt dolgok, tehát egy lapon a helyük; egy kételemű menücsoport helyett egy
// menüpont + két fül (a Pénzügy lap mintája, `routes/finance.tsx`).
//
// A lap CSAK HÉJ: a fülek `{ toolbar, content, primaryAction? }`-et adó hookok (`lib/tab-view.ts`), a héj
// pedig egy `AppShell`-be rakja őket. A rejtett fül lekérdezése el sem indul (`enabled`), tehát a
// fül-váltogatás nem terheli az Odin kérés-plafonját.
//
// JOG-KAPU FÜLENKÉNT: `service_assignments.view` és `service_catalog.view` — a fül CSAK akkor látszik, ha a
// joga megvan (nem letiltva: a letiltott fül hamis affordancia lenne, és jog-információt szivárogtatna).
// Egyik jog sem → a lap jog-hiba képernyőt ad. Az AKTÍV fül mindig olyan, amit a felhasználó látni is jogosult
// (egy kézzel írt `?tab=catalog` sem nyit olyat, amihez nincs joga).
import { QueueListIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { PageBody } from '@/components/shared/page-body';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useServiceAssignmentsView } from '@/features/service-assignments/use-service-assignments-view';
import { useServiceCatalogView } from '@/features/service-catalog/use-service-catalog-view';
import { useListFilters } from '@/hooks/use-list-filters';
import { requireAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/use-permissions';

/**
 * A LAP ÁLLAPOTA AZ URL-BEN — a fül és MINDKÉT fül szűrői (a repo state-konvenciója: a szűrt nézet legyen
 * linkelhető és F5-biztos). A route-nak ISMERNIE kell a kulcsokat, különben a router kiszűri őket.
 *
 * MIÉRT PREFIXES a szűrő-kulcs (`asg_` / `cat_`): egy címen KÉT lista ül, és mindkettőnek van keresője,
 * státusz-szűrője és rendezése — közös `q`/`status`/`sort` mellett a katalógus-keresés átúszna a
 * hozzárendelés-szűrőbe. Ugyanaz a szabály, mint a Pénzügy lapon (`exp_` / `rev_`). A fül-váltás így NEM
 * dobja el a másik fülön beállított szűrést.
 */
const servicesSearchSchema = z.object({
  tab: z.string().optional(),
  // Hozzárendelés-fül (a `asg_edit` / `asg_highlight` / `asg_client` deep-link paraméterek)
  asg_edit: z.string().optional(),
  asg_highlight: z.string().optional(),
  asg_client: z.string().optional(),
  asg_q: z.string().optional(),
  asg_account: z.string().optional(),
  asg_scope: z.string().optional(),
  asg_type: z.string().optional(),
  asg_status: z.string().optional(),
  asg_frequency: z.string().optional(),
  asg_agreement: z.string().optional(),
  asg_pricing: z.string().optional(),
  asg_sort: z.string().optional(),
  asg_order: z.string().optional(),
  // Katalógus-fül
  cat_edit: z.string().optional(),
  cat_q: z.string().optional(),
  cat_category: z.string().optional(),
  cat_status: z.string().optional(),
  cat_sort: z.string().optional(),
  cat_order: z.string().optional(),
});

export const Route = createFileRoute('/services')({
  validateSearch: (search) => servicesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: ServicesPage,
});

type ServicesTab = 'assignments' | 'catalog';

/** Exportált a route-teszt kedvéért (a `finance` mintája) — a route a `component`-en át használja. */
export function ServicesPage(): JSX.Element {
  const { t } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canAssignments = can('service_assignments.view');
  const canCatalog = can('service_catalog.view');
  const gated = !permsLoading && !canAssignments && !canCatalog;

  /**
   * A HÉJ állapota: a fül ÉS a két fül deep-link paraméterei. Mind a `useListFilters`-en át — ez a repo
   * URL-state mintája: a `defaults` egyben a kulcs-készlet, a default-értékű kulcs nem kerül az URL-be
   * (tiszta link), a navigáció `replace` (a fül-váltás nem history-bejegyzés). A deep-link TÖRLÉSE így
   * egyetlen `setShell({ … : '' })` — nem kell külön `useNavigate`.
   */
  const shellDefaults = useMemo(
    () => ({ tab: 'assignments', asg_edit: '', asg_client: '', asg_highlight: '', cat_edit: '' }),
    [],
  );
  const { filters: shell, setFilters: setShell } = useListFilters(shellDefaults);
  // Az üres string a „nincs deep-link" alak (a `useListFilters` így ejti ki az URL-ből) — a fülek
  // `undefined`-et várnak.
  const asgEdit = shell.asg_edit === '' ? undefined : shell.asg_edit;
  const asgClient = shell.asg_client === '' ? undefined : shell.asg_client;
  const asgHighlight = shell.asg_highlight === '' ? undefined : shell.asg_highlight;
  const catEdit = shell.cat_edit === '' ? undefined : shell.cat_edit;

  // AZ AKTÍV FÜL JOG-TUDATOS: ha a kért fülhöz nincs jog (kézzel írt `?tab=`), a másikra esünk vissza — a
  // kapuzás így nem csak a fül-sávon él, hanem azon is, MIT renderelünk.
  const requested: ServicesTab = shell.tab === 'catalog' ? 'catalog' : 'assignments';
  const tab: ServicesTab =
    requested === 'catalog'
      ? canCatalog
        ? 'catalog'
        : 'assignments'
      : canAssignments
        ? 'assignments'
        : 'catalog';

  // A deep-link param törlése a sheet záródásakor (back-barát + újra-nyitható) — fülönként a saját kulcsa.
  const clearAsgDeepLink = useCallback(() => setShell({ asg_edit: '' }), [setShell]);
  const clearCatDeepLink = useCallback(() => setShell({ cat_edit: '' }), [setShell]);

  // MINDKÉT hook MINDIG lefut (a hook-sorrend nem lehet feltételes) — a lekérdezéseiket az `enabled`
  // kapcsolja, tehát a rejtett fül nem kér adatot.
  const active = !gated && !permsLoading;
  const assignments = useServiceAssignmentsView({
    enabled: active && tab === 'assignments' && canAssignments,
    deepLinkEditId: asgEdit,
    initialClientId: asgClient,
    highlightId: asgHighlight,
    onClearDeepLink: clearAsgDeepLink,
  });
  const catalog = useServiceCatalogView({
    enabled: active && tab === 'catalog' && canCatalog,
    deepLinkEditId: catEdit,
    onClearDeepLink: clearCatDeepLink,
  });

  if (gated) {
    return (
      <AppShell>
        <PageBody align="left">
          <EmptyState
            title={t('services.forbiddenTitle')}
            description={t('services.forbiddenDesc')}
          />
        </PageBody>
      </AppShell>
    );
  }

  const view = tab === 'catalog' ? catalog : assignments;
  // A fül-sáv CSAK akkor jelenik meg, ha tényleg VAN választás: egyetlen látható fül mellett a sáv egy
  // kattinthatatlan díszlet lenne (és elárulná, hogy létezik egy másik fül).
  const showTabs = canAssignments && canCatalog;

  return (
    <AppShell
      {...(view.primaryAction ? { primaryAction: view.primaryAction } : {})}
      {...(view.secondaryActions ? { secondaryActions: view.secondaryActions } : {})}
      toolbar={
        <div className="flex flex-col">
          {showTabs && (
            <div className="px-4 pt-2">
              <Tabs value={tab} onValueChange={(v) => setShell({ tab: v })}>
                <TabsList>
                  <TabsTrigger value="assignments">
                    <Square3Stack3DIcon />
                    {t('services.tabs.assignments')}
                  </TabsTrigger>
                  <TabsTrigger value="catalog">
                    <QueueListIcon />
                    {t('services.tabs.catalog')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
          {view.toolbar}
        </div>
      }
    >
      {view.content}
    </AppShell>
  );
}
